import { Fragment, useMemo } from "react";
import type { CurrentResponse, LifetimeMap, MatchRow } from "@shared/api";
import { ChartState } from "@features/charts/ChartState";
import { aggregateMatches } from "@shared/lib/matchAggregate";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SLOT_DEFS = [
  { key: "late", label: "Late", start: 0, end: 6 },
  { key: "morning", label: "Morning", start: 6, end: 12 },
  { key: "afternoon", label: "Afternoon", start: 12, end: 18 },
  { key: "evening", label: "Evening", start: 18, end: 24 },
] as const;

interface InsightItem {
  title: string;
  value: string;
  detail: string;
  tone: "good" | "bad" | "neutral" | "accent";
}

interface MapPerformanceRow {
  name: string;
  image: string | null;
  rows: number;
  matches: number;
  kd: number;
  kpm: number;
  winRate: number | null;
  damagePerMatch: number;
}

interface TimeBucket {
  key: string;
  label: string;
  rows: MatchRow[];
  matches: number;
  kd: number;
  kpm: number;
  winRate: number | null;
}

interface HeatCell extends TimeBucket {
  day: string;
  slot: string;
}

interface ConsistencySummary {
  score: number;
  label: string;
  tone: "good" | "bad" | "neutral";
  avgKd: number;
  kdSpread: number;
  killSpread: number;
  mergedRate: number;
  trendDelta: number | null;
  bars: Array<{ id: number; kd: number; height: number; tone: "good" | "bad" | "neutral" }>;
}

export function InsightsFeed({ current, rows }: { current: CurrentResponse | null; rows: MatchRow[] | null }) {
  const insights = useMemo(() => (rows ? buildInsights(current, rows) : []), [current, rows]);

  return (
    <section className="insights-section">
      <div className="section-heading">
        <h2>Insights feed</h2>
      </div>
      {rows === null ? (
        <ChartState>Loading insights...</ChartState>
      ) : rows.length === 0 ? (
        <ChartState>No match rows in this range yet.</ChartState>
      ) : (
        <div className="insight-grid">
          {insights.map((insight) => (
            <article className={`insight-card tone-${insight.tone}`} key={insight.title}>
              <span>{insight.title}</span>
              <strong>{insight.value}</strong>
              <p>{insight.detail}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export function AdvancedInsights({ current, rows }: { current: CurrentResponse | null; rows: MatchRow[] }) {
  if (rows.length === 0) {
    return (
      <section className="advanced-insights-section">
        <h2>Situational insights</h2>
        <ChartState>No tracked match data in this range.</ChartState>
      </section>
    );
  }

  return (
    <section className="advanced-insights-section">
      <h2>Situational insights</h2>
      <div className="advanced-insight-grid">
        <ConsistencyScore rows={rows} />
        <DayTimePerformance rows={rows} />
        <MapPerformance current={current} rows={rows} />
      </div>
    </section>
  );
}

function ConsistencyScore({ rows }: { rows: MatchRow[] }) {
  const summary = useMemo(() => buildConsistency(rows), [rows]);

  return (
    <article className="advanced-panel consistency-panel">
      <div className="analytics-card-head">
        <h3>Consistency score</h3>
        <span>{rows.length.toLocaleString()} rows</span>
      </div>
      <div className={`consistency-score tone-${summary.tone}`}>
        <strong>{summary.score}</strong>
        <span>{summary.label}</span>
      </div>
      <div className="consistency-bars" aria-hidden="true">
        {summary.bars.map((bar) => (
          <i className={`tone-${bar.tone}`} key={bar.id} style={{ height: `${bar.height}%` }} />
        ))}
      </div>
      <div className="consistency-metrics">
        <InsightMetric label="Avg K/D" value={formatDecimal(summary.avgKd)} />
        <InsightMetric label="K/D spread" value={formatDecimal(summary.kdSpread)} />
        <InsightMetric label="Kill spread" value={formatDecimal(summary.killSpread)} />
        <InsightMetric label="Merged" value={`${(summary.mergedRate * 100).toFixed(0)}%`} />
      </div>
      <p className="advanced-note">
        {summary.trendDelta === null ? "Need more rows to compare recent stability." : `Recent K/D ${formatSigned(summary.trendDelta, formatDecimal)} vs the previous comparable block.`}
      </p>
    </article>
  );
}

function DayTimePerformance({ rows }: { rows: MatchRow[] }) {
  const performance = useMemo(() => buildTimePerformance(rows), [rows]);

  return (
    <article className="advanced-panel time-panel">
      <div className="analytics-card-head">
        <h3>Day and time performance</h3>
        <span>{performance.bestCell ? performance.bestCell.label : "No buckets"}</span>
      </div>
      <div className="time-summary-grid">
        <InsightMetric label="Best day" value={performance.bestDay ? `${performance.bestDay.label} / ${formatDecimal(performance.bestDay.kd)}` : "-"} />
        <InsightMetric label="Best time" value={performance.bestSlot ? `${performance.bestSlot.label} / ${formatDecimal(performance.bestSlot.kd)}` : "-"} />
        <InsightMetric label="Busiest" value={performance.busiest ? `${performance.busiest.label} / ${performance.busiest.matches}` : "-"} />
      </div>
      <div className="time-heatmap">
        <div className="time-heat-head" />
        {SLOT_DEFS.map((slot) => (
          <div className="time-heat-head" key={slot.key}>
            {slot.label}
          </div>
        ))}
        {DAY_LABELS.map((day) => (
          <Fragment key={day}>
            <div className="time-heat-day" key={`${day}-label`}>
              {day}
            </div>
            {SLOT_DEFS.map((slot) => {
              const cell = performance.cells.get(`${day}:${slot.key}`);
              return (
                <div className={heatClass(cell, performance.avgKd)} key={`${day}-${slot.key}`} title={cell ? `${cell.label}: ${cell.matches} matches, ${formatDecimal(cell.kd)} K/D` : `${day} ${slot.label}: no rows`}>
                  <strong>{cell && cell.matches > 0 ? formatDecimal(cell.kd) : "-"}</strong>
                  <span>{cell && cell.matches > 0 ? cell.matches.toLocaleString() : ""}</span>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </article>
  );
}

function MapPerformance({ current, rows }: { current: CurrentResponse | null; rows: MatchRow[] }) {
  const maps = useMemo(() => buildMapPerformance(rows, current?.breakdowns?.maps ?? []).slice(0, 6), [current, rows]);

  return (
    <div className="map-performance-block advanced-wide">
      <div className="analytics-card-head">
        <h3>Map performance</h3>
        <span>{maps.length.toLocaleString()} maps</span>
      </div>
      {maps.length === 0 ? (
        <ChartState>No map hints in this range.</ChartState>
      ) : (
        <div className="map-performance-grid">
          {maps.map((map) => (
            <article className="map-performance-card" key={map.name}>
              <MapArt image={map.image} name={map.name} />
              <div className="map-performance-copy">
                <div className="map-performance-title">
                  <strong>{map.name}</strong>
                  <span>{map.matches.toLocaleString()} matches</span>
                </div>
                <div className="map-performance-metrics">
                  <InsightMetric label="K/D" value={formatDecimal(map.kd)} />
                  <InsightMetric label="Win" value={formatPercent(map.winRate)} />
                  <InsightMetric label="Kills/Min" value={formatDecimal(map.kpm)} />
                  <InsightMetric label="Dmg/Match" value={formatCompact(map.damagePerMatch)} />
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="insight-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MapArt({ image, name }: { image: string | null; name: string }) {
  return (
    <div className="map-performance-art" aria-hidden="true">
      {image ? <img src={image} alt="" loading="lazy" onError={(event) => (event.currentTarget.style.display = "none")} /> : <span>{initials(name)}</span>}
    </div>
  );
}

function buildInsights(current: CurrentResponse | null, rows: MatchRow[]): InsightItem[] {
  const sorted = [...rows].sort((a, b) => a.window_end - b.window_end);
  const range = aggregateMatches(sorted);
  const consistency = buildConsistency(sorted);
  const insights: InsightItem[] = [];

  if (current) {
    const delta = (range.kd ?? 0) - current.derived.kd;
    insights.push({
      title: "Range vs lifetime",
      value: `${formatSigned(delta, formatDecimal)} K/D`,
      detail: `${range.observedMatches.toLocaleString()} tracked matches compared with lifetime ${formatDecimal(current.derived.kd)}.`,
      tone: delta > 0.05 ? "good" : delta < -0.05 ? "bad" : "neutral",
    });
  }

  const trendInsight = buildTrendInsight(sorted);
  if (trendInsight) insights.push(trendInsight);

  const topWeapon = topWeaponInsight(sorted);
  if (topWeapon) insights.push(topWeapon);

  const topMap = buildMapPerformance(sorted, current?.breakdowns?.maps ?? [])[0];
  if (topMap) {
    insights.push({
      title: "Best map in range",
      value: topMap.name,
      detail: `${formatDecimal(topMap.kd)} K/D, ${formatPercent(topMap.winRate)} win rate across ${topMap.matches.toLocaleString()} matches.`,
      tone: "accent",
    });
  }

  const time = buildTimePerformance(sorted);
  if (time.bestCell) {
    insights.push({
      title: "Best play window",
      value: time.bestCell.label,
      detail: `${formatDecimal(time.bestCell.kd)} K/D with ${time.bestCell.matches.toLocaleString()} observed matches.`,
      tone: "good",
    });
  }

  insights.push({
    title: "Consistency",
    value: `${consistency.score}/100`,
    detail: `${consistency.label}; ${formatDecimal(consistency.avgKd)} average K/D and ${(consistency.mergedRate * 100).toFixed(0)}% merged rows.`,
    tone: consistency.tone === "bad" ? "bad" : consistency.tone === "good" ? "good" : "neutral",
  });

  return insights.slice(0, 6);
}

function buildTrendInsight(rows: MatchRow[]): InsightItem | null {
  const blockSize = Math.min(10, Math.floor(rows.length / 2));
  if (blockSize < 3) return null;

  const previous = aggregateMatches(rows.slice(-blockSize * 2, -blockSize));
  const recent = aggregateMatches(rows.slice(-blockSize));
  const delta = (recent.kd ?? 0) - (previous.kd ?? 0);
  return {
    title: "Recent form",
    value: `${formatSigned(delta, formatDecimal)} K/D`,
    detail: `Last ${blockSize} rows vs the previous ${blockSize}; recent win rate ${formatPercent(recent.winRate)}.`,
    tone: delta > 0.1 ? "good" : delta < -0.1 ? "bad" : "neutral",
  };
}

function topWeaponInsight(rows: MatchRow[]): InsightItem | null {
  const totals = new Map<string, { name: string; kills: number; damage: number; rows: number }>();
  for (const row of rows) {
    for (const weapon of row.weapons ?? []) {
      const current = totals.get(weapon.id) ?? { name: weapon.name, kills: 0, damage: 0, rows: 0 };
      current.name = weapon.name;
      current.kills += Math.max(0, weapon.kills_delta);
      current.damage += Math.max(0, weapon.damage_delta);
      current.rows++;
      totals.set(weapon.id, current);
    }
  }

  const top = [...totals.values()].sort((a, b) => b.kills - a.kills || b.damage - a.damage || a.name.localeCompare(b.name))[0];
  if (!top || top.kills <= 0) return null;
  return {
    title: "Top weapon signal",
    value: top.name,
    detail: `${top.kills.toLocaleString()} kills and ${formatCompact(top.damage)} damage across ${top.rows.toLocaleString()} weapon rows.`,
    tone: "accent",
  };
}

function buildMapPerformance(rows: MatchRow[], lifetimeMaps: LifetimeMap[]): MapPerformanceRow[] {
  const groups = new Map<string, MatchRow[]>();
  const lifetimeByName = new Map(lifetimeMaps.map((map) => [normalizeName(map.name), map]));

  for (const row of rows) {
    if (!row.map_hint) continue;
    groups.set(row.map_hint, [...(groups.get(row.map_hint) ?? []), row]);
  }

  return [...groups.entries()]
    .map(([name, mapRows]) => {
      const aggregate = aggregateMatches(mapRows);
      const lifetime = lifetimeByName.get(normalizeName(name));
      return {
        name,
        image: lifetime?.image ?? null,
        rows: aggregate.rows,
        matches: aggregate.observedMatches,
        kd: aggregate.kd ?? 0,
        kpm: aggregate.killsPerMinute ?? 0,
        winRate: aggregate.winRate,
        damagePerMatch: aggregate.damagePerMatch ?? 0,
      };
    })
    .sort((a, b) => {
      const eligibleA = a.matches >= 2 ? 1 : 0;
      const eligibleB = b.matches >= 2 ? 1 : 0;
      return eligibleB - eligibleA || b.kd - a.kd || (b.winRate ?? 0) - (a.winRate ?? 0) || b.matches - a.matches || a.name.localeCompare(b.name);
    });
}

function buildTimePerformance(rows: MatchRow[]) {
  const dayBuckets = DAY_LABELS.map((label, index) => ({ key: String(index), label, rows: [] as MatchRow[] }));
  const slotBuckets = SLOT_DEFS.map((slot) => ({ key: slot.key, label: slot.label, rows: [] as MatchRow[] }));
  const cellBuckets = new Map<string, { key: string; label: string; rows: MatchRow[]; day: string; slot: string }>();

  for (const day of DAY_LABELS) {
    for (const slot of SLOT_DEFS) {
      const key = `${day}:${slot.key}`;
      cellBuckets.set(key, { key, label: `${day} ${slot.label}`, rows: [], day, slot: slot.key });
    }
  }

  for (const row of rows) {
    const date = new Date(row.window_end * 1000);
    const day = date.getDay();
    const hour = date.getHours();
    const slot = SLOT_DEFS.find((candidate) => hour >= candidate.start && hour < candidate.end) ?? SLOT_DEFS[0];
    const dayLabel = DAY_LABELS[day] ?? "Sun";
    dayBuckets[day]?.rows.push(row);
    slotBuckets.find((bucket) => bucket.key === slot.key)?.rows.push(row);
    cellBuckets.get(`${dayLabel}:${slot.key}`)?.rows.push(row);
  }

  const days = dayBuckets.map(toTimeBucket);
  const slots = slotBuckets.map(toTimeBucket);
  const cells = new Map<string, HeatCell>();
  for (const [key, bucket] of cellBuckets.entries()) {
    cells.set(key, { ...toTimeBucket(bucket), day: bucket.day, slot: bucket.slot });
  }

  const all = aggregateMatches(rows);
  const bestDay = bestBucket(days);
  const bestSlot = bestBucket(slots);
  const bestCell = bestBucket([...cells.values()]);
  const busiest = [...slots].sort((a, b) => b.matches - a.matches || b.rows.length - a.rows.length)[0] ?? null;

  return {
    days,
    slots,
    cells,
    bestDay,
    bestSlot,
    bestCell,
    busiest,
    avgKd: all.kd ?? 0,
  };
}

function toTimeBucket(bucket: { key: string; label: string; rows: MatchRow[] }): TimeBucket {
  const aggregate = aggregateMatches(bucket.rows);
  return {
    key: bucket.key,
    label: bucket.label,
    rows: bucket.rows,
    matches: aggregate.observedMatches,
    kd: aggregate.kd ?? 0,
    kpm: aggregate.killsPerMinute ?? 0,
    winRate: aggregate.winRate,
  };
}

function bestBucket<T extends TimeBucket>(buckets: T[]): T | null {
  const nonempty = buckets.filter((bucket) => bucket.matches > 0);
  const eligible = nonempty.filter((bucket) => bucket.matches >= 2);
  return (eligible.length > 0 ? eligible : nonempty).sort((a, b) => b.kd - a.kd || b.kpm - a.kpm || b.matches - a.matches)[0] ?? null;
}

function buildConsistency(rows: MatchRow[]): ConsistencySummary {
  const sorted = [...rows].sort((a, b) => a.window_end - b.window_end);
  const kdValues = sorted.map((row) => Math.min(8, rowKd(row)));
  const killValues = sorted.map((row) => row.kills_delta / Math.max(1, row.merged_match_count));
  const avgKd = mean(kdValues);
  const kdSpread = standardDeviation(kdValues);
  const killSpread = standardDeviation(killValues);
  const kdCv = coefficientOfVariation(kdValues);
  const killCv = coefficientOfVariation(killValues);
  const mergedRate = sorted.filter((row) => row.merged_match_count > 1 || row.derivation_kind === "merged").length / Math.max(1, sorted.length);
  const kdScore = 100 - clamp(kdCv * 42, 0, 100);
  const killScore = 100 - clamp(killCv * 36, 0, 100);
  const qualityScore = 100 - mergedRate * 35;
  const score = Math.round(clamp(kdScore * 0.5 + killScore * 0.32 + qualityScore * 0.18, 0, 100));
  const label = score >= 78 ? "Stable" : score >= 58 ? "Mixed" : "Volatile";
  const tone = score >= 78 ? "good" : score >= 58 ? "neutral" : "bad";
  const maxKd = Math.max(1, ...kdValues.slice(-18));
  const bars = sorted.slice(-18).map((row) => {
    const kd = Math.min(8, rowKd(row));
    const tone: "good" | "bad" | "neutral" = kd > avgKd * 1.08 ? "good" : kd < avgKd * 0.92 ? "bad" : "neutral";
    return {
      id: row.id,
      kd,
      height: clamp((kd / maxKd) * 100, 8, 100),
      tone,
    };
  });

  const blockSize = Math.min(8, Math.floor(kdValues.length / 2));
  const trendDelta = blockSize < 3 ? null : mean(kdValues.slice(-blockSize)) - mean(kdValues.slice(-blockSize * 2, -blockSize));

  return {
    score,
    label,
    tone,
    avgKd,
    kdSpread,
    killSpread,
    mergedRate,
    trendDelta,
    bars,
  };
}

function heatClass(cell: HeatCell | undefined, avgKd: number): string {
  const base = "time-heat-cell";
  if (!cell || cell.matches <= 0) return `${base} is-empty`;
  if (cell.kd >= avgKd * 1.12) return `${base} tone-good`;
  if (cell.kd <= avgKd * 0.88) return `${base} tone-bad`;
  return `${base} tone-neutral`;
}

function rowKd(row: MatchRow): number {
  return row.deaths_delta === 0 ? row.kills_delta : row.kills_delta / row.deaths_delta;
}

function mean(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function standardDeviation(values: number[]): number {
  if (values.length < 2) return 0;
  const avg = mean(values);
  return Math.sqrt(values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / values.length);
}

function coefficientOfVariation(values: number[]): number {
  const avg = mean(values);
  if (avg <= 0) return 0;
  return standardDeviation(values) / avg;
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

function formatDecimal(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCompact(value: number): string {
  return value.toLocaleString(undefined, { notation: "compact", maximumFractionDigits: 1 });
}

function formatPercent(value: number | null): string {
  return value === null ? "-" : `${(value * 100).toFixed(0)}%`;
}

function formatSigned(value: number, formatter: (value: number) => string): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatter(value)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 3)
    .toUpperCase();
}
